import type {
  CanonicalTreeGeometryBranchRoot,
} from './CanonicalTreeGeometryBranchRootAuthority';

export type CanonicalTreeConnectorRoot =
  | 'maternal'
  | 'paternal'
  | 'self'
  | 'both'
  | 'descendant'
  | 'partner-affiliated'
  | 'child-mediated'
  | 'affiliated'
  | 'unresolved';

export interface CanonicalTreeConnectorPoint {
  readonly x:
    number;

  readonly y:
    number;
}

export interface CanonicalTreeConnectorRectangle {
  readonly memberId:
    string;

  readonly centerX:
    number;

  readonly centerY:
    number;

  readonly width:
    number;

  readonly height:
    number;
}

export interface CanonicalTreeConnectorCorridorInput {
  readonly key:
    string;

  readonly fromMemberIds:
    readonly string[];

  readonly toMemberId:
    string;

  /**
   * Exact structural viewer identity.
   *
   * This does not grant general root-boundary bypass authority.
   * It permits a maternal/paternal ancestry corridor to enter
   * the viewer envelope only when that exact viewer is the
   * terminal child endpoint.
   */
  readonly viewerMemberId?:
    string | null;

  /**
   * Explicit topology purpose supplied by the connector caller.
   *
   * Absence means ordinary connector authority. Geometry, tier,
   * and target identity are never sufficient by themselves to
   * acquire viewer-terminal ingress authority.
   */
  readonly endpointPurpose?:
    'DIRECT_PARENT_TO_VIEWER' | null;

  readonly root:
    CanonicalTreeConnectorRoot;

  readonly from:
    CanonicalTreeConnectorPoint;

  readonly to:
    CanonicalTreeConnectorPoint;

  readonly parentTier:
    number;

  readonly childTier:
    number;

  readonly viewerCenterX:
    number;

  readonly viewerEnvelopeWidth:
    number;

  readonly rootGap:
    number;

  readonly obstacles:
    readonly CanonicalTreeConnectorRectangle[];
}

export type CanonicalTreeConnectorRouteStatus =
  | 'ROUTED'
  | 'REJECTED-NONADJACENT-GENERATION'
  | 'REJECTED-ROOT-BOUNDARY'
  | 'REJECTED-OBSTACLE-INTERSECTION';

export const CANONICAL_TREE_VIEWER_TERMINAL_FAILURE_KEYS =
  Object.freeze([
    'ENDPOINT_PURPOSE_NOT_DIRECT_PARENT_TO_VIEWER',
    'ROOT_NOT_ANCESTRY',
    'VIEWER_MEMBER_ID_MISSING',
    'TARGET_NOT_VIEWER',
    'NOT_DIRECT_PARENT_TIER',
    'TARGET_OUTSIDE_VIEWER_ENVELOPE',
    'MATERNAL_SOURCE_NOT_LEFT_OF_VIEWER_ENVELOPE',
    'PATERNAL_SOURCE_NOT_RIGHT_OF_VIEWER_ENVELOPE',
  ] as const);

export type CanonicalTreeViewerTerminalFailureKey =
  typeof CANONICAL_TREE_VIEWER_TERMINAL_FAILURE_KEYS[number];

export interface CanonicalTreeViewerTerminalEligibilityEvidence {
  readonly candidate:
    boolean;

  readonly eligible:
    boolean;

  readonly failureReasons:
    readonly CanonicalTreeViewerTerminalFailureKey[];
}

export interface CanonicalTreeConnectorCorridorResult {
  readonly status:
    CanonicalTreeConnectorRouteStatus;

  readonly key:
    string;

  readonly root:
    CanonicalTreeConnectorRoot;

  readonly fromMemberIds:
    readonly string[];

  readonly toMemberId:
    string;

  readonly points:
    readonly CanonicalTreeConnectorPoint[];

  readonly corridorY:
    number | null;

  readonly rejectedObstacleMemberIds:
    readonly string[];

  /*
   * Diagnostic-only explanation of the existing viewer-terminal
   * exception. This field cannot change route status or geometry.
   */
  readonly viewerTerminalEligibility:
    CanonicalTreeViewerTerminalEligibilityEvidence;
}

const TOLERANCE =
  0.001;

const finite = (
  value:
    number,
  name:
    string,
): number => {
  if (!Number.isFinite(value)) {
    throw new Error(
      `Invalid connector geometry value: ${name}.`,
    );
  }

  return value;
};

const nonNegative = (
  value:
    number,
  name:
    string,
): number => {
  const checked =
    finite(
      value,
      name,
    );

  if (checked < 0) {
    throw new Error(
      `Connector geometry value ${name} must be non-negative.`,
    );
  }

  return checked;
};

const isAncestryRoot = (
  root:
    CanonicalTreeGeometryBranchRoot,
): root is 'maternal' | 'paternal' =>
  root === 'maternal' ||
  root === 'paternal';

const pointInsideRectangle = (
  point:
    CanonicalTreeConnectorPoint,

  rectangle:
    CanonicalTreeConnectorRectangle,
): boolean => {
  const halfWidth =
    rectangle.width / 2;

  const halfHeight =
    rectangle.height / 2;

  return (
    point.x >
      rectangle.centerX -
        halfWidth +
        TOLERANCE &&
    point.x <
      rectangle.centerX +
        halfWidth -
        TOLERANCE &&
    point.y >
      rectangle.centerY -
        halfHeight +
        TOLERANCE &&
    point.y <
      rectangle.centerY +
        halfHeight -
        TOLERANCE
  );
};

const axisAlignedSegmentIntersectsRectangle = (
  start:
    CanonicalTreeConnectorPoint,

  end:
    CanonicalTreeConnectorPoint,

  rectangle:
    CanonicalTreeConnectorRectangle,
): boolean => {
  const left =
    rectangle.centerX -
    rectangle.width / 2;

  const right =
    rectangle.centerX +
    rectangle.width / 2;

  const top =
    rectangle.centerY -
    rectangle.height / 2;

  const bottom =
    rectangle.centerY +
    rectangle.height / 2;

  if (
    Math.abs(
      start.x -
      end.x,
    ) <= TOLERANCE
  ) {
    const x =
      start.x;

    const minY =
      Math.min(
        start.y,
        end.y,
      );

    const maxY =
      Math.max(
        start.y,
        end.y,
      );

    return (
      x >
        left +
          TOLERANCE &&
      x <
        right -
          TOLERANCE &&
      maxY >
        top +
          TOLERANCE &&
      minY <
        bottom -
          TOLERANCE
    );
  }

  if (
    Math.abs(
      start.y -
      end.y,
    ) <= TOLERANCE
  ) {
    const y =
      start.y;

    const minX =
      Math.min(
        start.x,
        end.x,
      );

    const maxX =
      Math.max(
        start.x,
        end.x,
      );

    return (
      y >
        top +
          TOLERANCE &&
      y <
        bottom -
          TOLERANCE &&
      maxX >
        left +
          TOLERANCE &&
      minX <
        right -
          TOLERANCE
    );
  }

  throw new Error(
    'Canonical connector segments must remain orthogonal.',
  );
};

const routeCrossesRootBoundary = (
  points:
    readonly CanonicalTreeConnectorPoint[],

  root:
    CanonicalTreeConnectorRoot,

  viewerCenterX:
    number,

  viewerEnvelopeWidth:
    number,

  rootGap:
    number,
): boolean => {
  if (!isAncestryRoot(root)) {
    return false;
  }

  const viewerLeft =
    viewerCenterX -
    viewerEnvelopeWidth / 2;

  const viewerRight =
    viewerCenterX +
    viewerEnvelopeWidth / 2;

  if (root === 'maternal') {
    const maximumAllowedX =
      viewerLeft -
      rootGap;

    return points.some(
      point =>
        point.x >
        maximumAllowedX +
          TOLERANCE,
    );
  }

  const minimumAllowedX =
    viewerRight +
    rootGap;

  return points.some(
    point =>
      point.x <
      minimumAllowedX -
        TOLERANCE,
  );
};

const classifyViewerTerminalEligibility = (
  input:
    CanonicalTreeConnectorCorridorInput,

  from:
    CanonicalTreeConnectorPoint,

  to:
    CanonicalTreeConnectorPoint,

  viewerCenterX:
    number,

  viewerEnvelopeWidth:
    number,
): CanonicalTreeViewerTerminalEligibilityEvidence => {
  const failureReasons:
    CanonicalTreeViewerTerminalFailureKey[] = [];

  const candidate =
    input.endpointPurpose ===
      'DIRECT_PARENT_TO_VIEWER';

  const ancestryRoot =
    isAncestryRoot(
      input.root,
    );

  const viewerPresent =
    typeof input.viewerMemberId ===
      'string' &&
    input.viewerMemberId.length > 0;

  const viewerLeft =
    viewerCenterX -
    viewerEnvelopeWidth / 2;

  const viewerRight =
    viewerCenterX +
    viewerEnvelopeWidth / 2;

  if (!candidate) {
    failureReasons.push(
      'ENDPOINT_PURPOSE_NOT_DIRECT_PARENT_TO_VIEWER',
    );
  }

  if (!ancestryRoot) {
    failureReasons.push(
      'ROOT_NOT_ANCESTRY',
    );
  }

  if (!viewerPresent) {
    failureReasons.push(
      'VIEWER_MEMBER_ID_MISSING',
    );
  }

  if (
    !viewerPresent ||
    input.toMemberId !==
      input.viewerMemberId
  ) {
    failureReasons.push(
      'TARGET_NOT_VIEWER',
    );
  }

  if (
    input.parentTier !== 1 ||
    input.childTier !== 0
  ) {
    failureReasons.push(
      'NOT_DIRECT_PARENT_TIER',
    );
  }

  if (
    to.x <
      viewerLeft -
        TOLERANCE ||
    to.x >
      viewerRight +
        TOLERANCE
  ) {
    failureReasons.push(
      'TARGET_OUTSIDE_VIEWER_ENVELOPE',
    );
  }

  if (
    ancestryRoot &&
    input.root === 'maternal' &&
    !(
      from.x <
      viewerLeft -
        TOLERANCE
    )
  ) {
    failureReasons.push(
      'MATERNAL_SOURCE_NOT_LEFT_OF_VIEWER_ENVELOPE',
    );
  }

  if (
    ancestryRoot &&
    input.root === 'paternal' &&
    !(
      from.x >
      viewerRight +
        TOLERANCE
    )
  ) {
    failureReasons.push(
      'PATERNAL_SOURCE_NOT_RIGHT_OF_VIEWER_ENVELOPE',
    );
  }

  return Object.freeze({
    candidate,
    eligible:
      failureReasons.length === 0,
    failureReasons:
      Object.freeze([
        ...failureReasons,
      ]),
  });
};

export const planCanonicalTreeConnectorCorridor = (
  input:
    CanonicalTreeConnectorCorridorInput,
): CanonicalTreeConnectorCorridorResult => {
  const from =
    Object.freeze({
      x:
        finite(
          input.from.x,
          'from.x',
        ),

      y:
        finite(
          input.from.y,
          'from.y',
        ),
    });

  const to =
    Object.freeze({
      x:
        finite(
          input.to.x,
          'to.x',
        ),

      y:
        finite(
          input.to.y,
          'to.y',
        ),
    });

  const viewerCenterX =
    finite(
      input.viewerCenterX,
      'viewerCenterX',
    );

  const viewerEnvelopeWidth =
    nonNegative(
      input.viewerEnvelopeWidth,
      'viewerEnvelopeWidth',
    );

  const rootGap =
    nonNegative(
      input.rootGap,
      'rootGap',
    );

  const viewerTerminalEligibility =
    classifyViewerTerminalEligibility(
      input,
      from,
      to,
      viewerCenterX,
      viewerEnvelopeWidth,
    );

  if (
    input.parentTier !==
    input.childTier + 1
  ) {
    return Object.freeze({
      status:
        'REJECTED-NONADJACENT-GENERATION',

      viewerTerminalEligibility,

      key:
        input.key,

      root:
        input.root,

      fromMemberIds:
        Object.freeze([
          ...input.fromMemberIds,
        ]),

      toMemberId:
        input.toMemberId,

      points:
        Object.freeze([]),

      corridorY:
        null,

      rejectedObstacleMemberIds:
        Object.freeze([]),
    });
  }

  const corridorY =
    from.y +
    (
      to.y -
      from.y
    ) / 2;

  const points =
    Object.freeze([
      from,

      Object.freeze({
        x:
          from.x,

        y:
          corridorY,
      }),

      Object.freeze({
        x:
          to.x,

        y:
          corridorY,
      }),

      to,
    ]);

  /*
   * Preserve the historical route decision exactly.
   * 003G3E2 exposes only why the already-existing
   * viewer-terminal predicate did or did not qualify.
   */
  const validViewerTerminal =
    viewerTerminalEligibility.eligible;

  if (
    !validViewerTerminal &&
    routeCrossesRootBoundary(
      points,
      input.root,
      viewerCenterX,
      viewerEnvelopeWidth,
      rootGap,
    )
  ) {
    return Object.freeze({
      status:
        'REJECTED-ROOT-BOUNDARY',

      viewerTerminalEligibility,

      key:
        input.key,

      root:
        input.root,

      fromMemberIds:
        Object.freeze([
          ...input.fromMemberIds,
        ]),

      toMemberId:
        input.toMemberId,

      points:
        Object.freeze([]),

      corridorY:
        null,

      rejectedObstacleMemberIds:
        Object.freeze([]),
    });
  }

  const sourceMemberIds =
    new Set(
      input.fromMemberIds,
    );

  const ignoredMemberIds =
    new Set([
      ...sourceMemberIds,
      input.toMemberId,
    ]);

  const obstacles =
    input.obstacles.map(
      obstacle =>
        Object.freeze({
          memberId:
            obstacle.memberId,

          centerX:
            finite(
              obstacle.centerX,
              `${obstacle.memberId}.centerX`,
            ),

          centerY:
            finite(
              obstacle.centerY,
              `${obstacle.memberId}.centerY`,
            ),

          width:
            nonNegative(
              obstacle.width,
              `${obstacle.memberId}.width`,
            ),

          height:
            nonNegative(
              obstacle.height,
              `${obstacle.memberId}.height`,
            ),
        }),
    );

  const rejectedObstacleMemberIds =
    new Set<string>();

  for (const obstacle of obstacles) {
    if (
      ignoredMemberIds.has(
        obstacle.memberId,
      )
    ) {
      continue;
    }

    if (
      points.some(
        point =>
          pointInsideRectangle(
            point,
            obstacle,
          ),
      )
    ) {
      rejectedObstacleMemberIds.add(
        obstacle.memberId,
      );

      continue;
    }

    for (
      let index = 1;
      index < points.length;
      index += 1
    ) {
      if (
        axisAlignedSegmentIntersectsRectangle(
          points[index - 1],
          points[index],
          obstacle,
        )
      ) {
        rejectedObstacleMemberIds.add(
          obstacle.memberId,
        );

        break;
      }
    }
  }

  if (
    rejectedObstacleMemberIds.size >
    0
  ) {
    return Object.freeze({
      status:
        'REJECTED-OBSTACLE-INTERSECTION',

      viewerTerminalEligibility,

      key:
        input.key,

      root:
        input.root,

      fromMemberIds:
        Object.freeze([
          ...input.fromMemberIds,
        ]),

      toMemberId:
        input.toMemberId,

      points:
        Object.freeze([]),

      corridorY:
        null,

      rejectedObstacleMemberIds:
        Object.freeze(
          Array.from(
            rejectedObstacleMemberIds,
          ).sort(),
        ),
    });
  }

  return Object.freeze({
    status:
      'ROUTED',

    viewerTerminalEligibility,

    key:
      input.key,

    root:
      input.root,

    fromMemberIds:
      Object.freeze([
        ...input.fromMemberIds,
      ]),

    toMemberId:
      input.toMemberId,

    points,

    corridorY,

    rejectedObstacleMemberIds:
      Object.freeze([]),
  });
};
